import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {z} from 'zod';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {ContentCardScratchpadState} from '../../scratchpad/contentCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {NodeOutputData, NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import {ContentSelectionStrategyValue} from 'common/src/modules/contentFeeds/contentFeeds.enums';
import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import describeIdFormat from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/describeIdFormat';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import formatParseError from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/formatParseError';
import {IdPrefix} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.enums';
import {idHelper} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.config';

const inputSchema = z.object({
  strategyId: z.string().describe(describeIdFormat(IdPrefix.ContentStrategy)),
});

const contentStrategyById: Record<number, ContentSelectionStrategyValue> = {
  0: ContentSelectionStrategyValue.Specific,
  1: ContentSelectionStrategyValue.Matching,
  2: ContentSelectionStrategyValue.ContentSetRandom,
};

export default function setContentStrategyTool() {
  async function execute(
    params: ToolExecuteParams<typeof inputSchema, ContentCardScratchpadState>,
  ): Promise<ToolResult> {
    const {
      input: {strategyId},
      toolUtils: {agentScratchpad},
    } = params;
    const validatedScratchpad = validateAgentScratchpad<ContentCardScratchpadState>(
      agentScratchpad,
      NodeType.CONTENT_FEED,
    );

    const parseResult = idHelper.parseContentStrategyId(strategyId);

    if (parseResult.ok === false) {
      return toolError(formatParseError(parseResult.error));
    }

    const parsedStrategyId = parseResult.numericId;

    if (!(parsedStrategyId in contentStrategyById)) {
      return toolError(
        `Invalid content strategy ID: ${strategyId}. Valid values are ${Object.keys(
          contentStrategyById,
        )
          .map((id) => idHelper.toContentStrategyId(+id))
          .join(', ')}`,
      );
    }

    const strategy = contentStrategyById[parsedStrategyId];

    validatedScratchpad.card.strategy = strategy;

    // Rebuild outputs with correct ordering: Success, Partial Success (if custom matching), Fallback
    const fallbackOutput = validatedScratchpad.card.outputs.find(
      (o) => o.type === OutputType.Fallback,
    );
    const successOutput = validatedScratchpad.card.outputs.find(
      (o) => o.type === OutputType.Regular,
    );

    const newOutputs: NodeOutputData[] = [];

    // Add Success output (always present)
    if (successOutput) {
      newOutputs.push(successOutput);
    } else {
      newOutputs.push({
        id: 1,
        name: 'Success',
        type: OutputType.Regular,
        nodeId: NO_NODE_ID,
      });
    }

    // Add Fallback output (always present)
    if (fallbackOutput) {
      newOutputs.push({...fallbackOutput, id: newOutputs.length + 1});
    } else {
      newOutputs.push({
        id: newOutputs.length + 1,
        name: 'Fallback',
        type: OutputType.Fallback,
        nodeId: NO_NODE_ID,
      });
    }

    validatedScratchpad.card.outputs = newOutputs;

    return toolResult(
      `Content selection strategy set to ${idHelper.toContentStrategyId(parsedStrategyId)}, and outputs rebuilt`,
    );
  }

  return tool({
    name: V2CardAgentToolName.SetContentStrategy,
    displayName: 'Setting content strategy',
    description: 'Set the content selection strategy using a prefixed strategy ID',
    inputSchema,
    execute,
  });
}
